// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import { Box, Grid2, Menu, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseAvatar, BaseDatePicker, BaseStatus, BaseTable, Loading } from '~/components/common';
// Others
import { formatDate, formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { StorageEnum } from '~/utils/enum/base';
import { getListTruck } from '~/thunks/truck/truckThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { googleMapApiKey } from '~/utils/constants/env';
import { IHeaderCellType } from '~/utils/interface/common';
import { IMarkerLocation } from '~/utils/interface/statistic';
import { IQueryParamTruck } from '~/utils/interface/truck';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IListDataResponse, IPaginationResponse } from '~/utils/interface/base';
import { DateFormatEnum, MapDashboardStatus, RoleEnum } from '~/utils/enum/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  DEFAULT_LIMIT_PAGE_DASHBOARD,
  DEFAULT_MAX_PAGE,
  DEFAULT_POSITION,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { getListOrderEvent } from '~/thunks/order/orderThunk';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { IOrderEventList, IQueryParamOrderEvent } from '~/utils/interface/order';
// Styles, images, icons
import styles from './DeliveryMap.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsJobs = (
  t: TFunction,
  onDetailOrder: (job: IOrderEventList) => void
): IHeaderCellType<IOrderEventList>[] => {
  return [
    {
      id: 'title',
      label: t('table_header_title_title'),
      render: (record: IOrderEventList) => record?.eventTitle || EMPTY_VALUE,
      minWidth: 200,
    },
    {
      id: 'order',
      label: t('table_header_title_order'),
      render: (record: IOrderEventList) => {
        return (
          <Typography className={cx('textOrder')} onClick={() => onDetailOrder(record)}>
            {record?.orderId ? `#${record?.orderId}` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'client',
      label: t('table_header_title_client'),
      render: (record: IOrderEventList) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.client} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.client?.firstName,
                  lastName: record?.client?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'type',
      label: t('table_header_title_type'),
      render: (record: IOrderEventList) => {
        return (
          <Typography className={cx('textAddress')}>
            {record?.isDelivery ? t('delivery_map_title_delivery') : t('delivery_map_title_pickup')}
          </Typography>
        );
      },
      minWidth: 120,
    },
    {
      id: 'phoneNumber',
      label: t('table_header_title_phone'),
      render: (record: IOrderEventList) =>
        formatPhoneNumber(record?.client?.clientContacts?.[0]?.phoneNumber) || EMPTY_VALUE,
      minWidth: 150,
    },
    {
      id: 'timeline',
      label: t('table_header_title_timeline'),
      render: (record: IOrderEventList) => {
        return `${formatDate(record?.startDate, DateFormatEnum.M_D_YYYY)} - ${record?.startTime || EMPTY_STRING}`;
      },
      minWidth: 180,
    },
    {
      id: 'address',
      label: t('table_header_title_address'),
      render: (record: IOrderEventList) => {
        return <Typography className={cx('textAddress')}>{record?.address?.address || EMPTY_VALUE}</Typography>;
      },
      minWidth: 200,
    },
    {
      id: 'status',
      label: t('table_header_title_status'),
      render: (record: IOrderEventList) => {
        return <BaseStatus label={record?.status} />;
      },
    },
  ];
};

const DeliveryMap = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  // useEffect(() => {
  //   if (!isLoaded) return;

  //   const directionsService = new google.maps.DirectionsService();

  //   directionsService.route(
  //     {
  //       origin: positionSelected[0],
  //       destination: positionSelected[positionSelected.length - 1],
  //       waypoints: positionSelected.slice(1, -1).map((loc) => ({ location: loc, stopover: true })),
  //       travelMode: google.maps.TravelMode.DRIVING,
  //     },
  //     (result, status) => {
  //       if (status === google.maps.DirectionsStatus.OK && result) {
  //         setDirections(result);
  //       } else {
  //         console.error('Error fetching directions', result);
  //       }
  //     }
  //   );
  // }, [isLoaded]);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [paramObject, setParamObject] = useState<IQueryParamOrderEvent>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE_DASHBOARD,
    startDate: moment().format(DateFormatEnum.YYYY_MM_DD),
    endDate: moment().format(DateFormatEnum.YYYY_MM_DD),
    isAll: true,
  });

  const [listJob, setListJob] = useState<IOrderEventList[]>();

  const [positionJob, setPositionJob] = useState<IMarkerLocation[]>([]);
  const [positionTruck, setPositionTruck] = useState<IMarkerLocation[]>([]);
  const [positionIndex, setPositionIndex] = useState<number>(0);

  const [markerMenu, setMarkerMenu] = useState<{
    anchorEl: HTMLElement | null;
    marker: IMarkerLocation | null;
  }>({ anchorEl: null, marker: null });
  //#endregion Declare State

  //#region Declare Memo
  const positionSelected = useMemo(() => {
    return [...positionJob, ...positionTruck];
  }, [positionJob, positionTruck]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListJob(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    const params: IQueryParamTruck = {
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_MAX_PAGE,
    };

    handleGetListTruck(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListJob = (params: IQueryParamOrderEvent) => {
    setIsLoading(true);

    dispatch(getListOrderEvent(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res?.data as IListDataResponse<IOrderEventList[]>;

        if (!responses) return;

        setListJob(responses);
        setPagination(pagination);

        setPositionJob(
          responses?.map((item) => ({
            lat: Number(item?.address?.lat),
            lng: Number(item?.address?.lng) + (item?.isDelivery ? DEFAULT_POSITION : -DEFAULT_POSITION),
            type: item?.isDelivery ? MapDashboardStatus.DELIVERY : MapDashboardStatus.PICKUP,
            job: item,
          }))
        );
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGetListTruck = (params: IQueryParamTruck) => {
    setIsLoading(true);

    dispatch(getListTruck(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        setPositionTruck([
          ...responses.map((item) => ({
            lat: Number(item?.lat) - DEFAULT_POSITION,
            lng: Number(item?.lng) - DEFAULT_POSITION,
            type: MapDashboardStatus.TRUCK,
            truck: item,
          })),
        ]);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleView = (_id: string, _data: IOrderEventList, index: number) => {
    setPositionIndex(index);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handleChangeDate = (date: string | { startDate: string; endDate: string }) => {
    if (typeof date !== 'string') {
      setParamObject((prev) => ({
        ...prev,
        startDate: date.startDate,
        endDate: date.endDate,
      }));
    }
  };

  const handleDetailOrder = (job?: IOrderEventList) => {
    if (!job?.orderId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailOrder(job?.orderId)}`);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='deliveryMapComponent' className={cx('container')}>
      <Stack direction={'column'} spacing={10}>
        <Stack direction={'row'} gap={4} marginTop={'10px'} marginBottom={3} justifyContent={'space-between'}>
          <Typography className={cx('title')}>{t('dashboard_title_delivery_map')}</Typography>

          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <img src={icons.commonIconDelivery} alt={t('common_alt_img')} />

              <Typography className={cx('noteMarker')}>{t('delivery_map_title_delivery')}</Typography>
            </Stack>

            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <img src={icons.commonIconPickup} alt={t('common_alt_img')} />

              <Typography className={cx('noteMarker')}>{t('delivery_map_title_pickup')}</Typography>
            </Stack>

            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <img src={icons.commonIconPositionTruck} alt={t('common_alt_img')} />

              <Typography className={cx('noteMarker')}>{t('delivery_map_title_truck')}</Typography>
            </Stack>

            <BaseDatePicker
              height={36}
              mode={'rangeDate'}
              value={{
                startDate: paramObject?.startDate || EMPTY_STRING,
                endDate: paramObject?.endDate || EMPTY_STRING,
              }}
              onChange={handleChangeDate}
            />
          </Stack>
        </Stack>

        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: 450, borderRadius: 20 }}
            center={positionSelected[positionIndex]}
            zoom={14}
          >
            {positionSelected.map((location, index) => (
              <Marker
                key={index}
                position={location}
                onClick={(e: google.maps.MapMouseEvent) => {
                  setMarkerMenu({
                    anchorEl: e.domEvent.currentTarget as HTMLElement,
                    marker: location,
                  });
                }}
                icon={{
                  url:
                    'data:image/svg+xml;charset=UTF-8,' +
                    encodeURIComponent(
                      location.type === MapDashboardStatus.TRUCK
                        ? `<svg width="50" height="50" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path 
                            fill="#344054"
                            d="
                            M12 2
                            C8.13 2 5 5.13 5 9
                            c0 5.25 7 13 7 13
                            s7-7.75 7-13
                            c0-3.87-3.13-7-7-7z"
                          />

                          <g transform="translate(6.5,5) scale(0.45)">
                            <path 
                              d="M10.75 5C10.5511 5 10.3603 5.07902 10.2197 5.21967C10.079 5.36032 10 5.55109 10 5.75C10 5.94891 10.079 6.13968 10.2197 6.28033C10.3603 6.42098 10.5511 6.5 10.75 6.5H13.25C13.4489 6.5 13.6397 6.42098 13.7803 6.28033C13.921 6.13968 14 5.94891 14 5.75C14 5.55109 13.921 5.36032 13.7803 5.21967C13.6397 5.07902 13.4489 5 13.25 5H10.75ZM4 5.75C4 4.75544 4.39509 3.80161 5.09835 3.09835C5.80161 2.39509 6.75544 2 7.75 2H16.25C17.2446 2 18.1984 2.39509 18.9017 3.09835C19.6049 3.80161 20 4.75544 20 5.75V9.5H21.227C21.4259 9.5 21.6167 9.57902 21.7573 9.71967C21.898 9.86032 21.977 10.0511 21.977 10.25C21.977 10.4489 21.898 10.6397 21.7573 10.7803C21.6167 10.921 21.4259 11 21.227 11H20V19.75C20 20.2141 19.8156 20.6592 19.4874 20.9874C19.1592 21.3156 18.7141 21.5 18.25 21.5H16.75C16.2859 21.5 15.8408 21.3156 15.5126 20.9874C15.1844 20.6592 15 20.2141 15 19.75V18.5H9V19.75C9 20.2141 8.81563 20.6592 8.48744 20.9874C8.15925 21.3156 7.71413 21.5 7.25 21.5H5.75C5.28587 21.5 4.84075 21.3156 4.51256 20.9874C4.18437 20.6592 4 20.2141 4 19.75V11H2.75C2.55109 11 2.36032 10.921 2.21967 10.7803C2.07902 10.6397 2 10.4489 2 10.25C2 10.0511 2.07902 9.86032 2.21967 9.71967C2.36032 9.57902 2.55109 9.5 2.75 9.5H4V5.75ZM16.5 18.5V19.75C16.5 19.888 16.612 20 16.75 20H18.25C18.3163 20 18.3799 19.9737 18.4268 19.9268C18.4737 19.8799 18.5 19.8163 18.5 19.75V18.5H16.5ZM5.5 18.5V19.75C5.5 19.888 5.612 20 5.75 20H7.25C7.3163 20 7.37989 19.9737 7.42678 19.9268C7.47366 19.8799 7.5 19.8163 7.5 19.75V18.5H5.5ZM7.75 3.5C7.15326 3.5 6.58097 3.73705 6.15901 4.15901C5.73705 4.58097 5.5 5.15326 5.5 5.75V12H18.5V5.75C18.5 5.15326 18.2629 4.58097 17.841 4.15901C17.419 3.73705 16.8467 3.5 16.25 3.5H7.75ZM9 15C9 14.7348 8.89464 14.4804 8.70711 14.2929C8.51957 14.1054 8.26522 14 8 14C7.73478 14 7.48043 14.1054 7.29289 14.2929C7.10536 14.4804 7 14.7348 7 15C7 15.2652 7.10536 15.5196 7.29289 15.7071C7.48043 15.8946 7.73478 16 8 16C8.26522 16 8.51957 15.8946 8.70711 15.7071C8.89464 15.5196 9 15.2652 9 15ZM16 16C16.2652 16 16.5196 15.8946 16.7071 15.7071C16.8946 15.5196 17 15.2652 17 15C17 14.7348 16.8946 14.4804 16.7071 14.2929C16.5196 14.1054 16.2652 14 16 14C15.7348 14 15.4804 14.1054 15.2929 14.2929C15.1054 14.4804 15 14.7348 15 15C15 15.2652 15.10536 15.5196 15.2929 15.7071C15.4804 15.8946 15.7348 16 16 16Z"
                              fill="white"
                            />
                          </g>
                          </svg>`
                        : `
                        <svg width="50" height="50" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path fill="${location.type === MapDashboardStatus.DELIVERY ? '#FF6600' : '#007BFF'}" d="
                            M12 2
                            C8.13 2 5 5.13 5 9
                            c0 5.25 7 13 7 13
                            s7-7.75 7-13
                            c0-3.87-3.13-7-7-7z"
                          />
                          <circle cx="12" cy="9" r="4" fill="${
                            location.type === MapDashboardStatus.DELIVERY ? '#CC5200' : '#0051B8'
                          }"/>
                        </svg>
                  `
                    ),
                }}
              />
            ))}

            {markerMenu.marker && (
              <Menu
                open={Boolean(markerMenu.anchorEl)}
                anchorEl={markerMenu.anchorEl}
                onClose={() => setMarkerMenu({ anchorEl: null, marker: null })}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                autoFocus={false}
                slotProps={{
                  paper: { className: cx('paper') },
                }}
                MenuListProps={{ className: cx('menuList') }}
              >
                <Stack className={cx('viewModal')} gap={2}>
                  <Typography className={cx('titleItem')}>{markerMenu.marker.type.toUpperCase()}</Typography>

                  {markerMenu.marker.type === MapDashboardStatus.TRUCK && (
                    <Typography className={cx('titleSub')}>{markerMenu.marker.truck?.name || EMPTY_VALUE}</Typography>
                  )}

                  {markerMenu.marker.type !== MapDashboardStatus.TRUCK && (
                    <>
                      <Typography className={cx('titleSub')}>
                        {markerMenu.marker.job?.eventTitle || EMPTY_VALUE}
                      </Typography>

                      <Typography className={cx('titleSub')}>
                        {`${formatDate(markerMenu.marker.job?.startDate, DateFormatEnum.M_D_YYYY)} - ${
                          markerMenu.marker.job?.startTime || EMPTY_STRING
                        }`}
                      </Typography>

                      <Typography
                        className={cx('textOrder')}
                        onClick={() => handleDetailOrder(markerMenu?.marker?.job)}
                      >
                        {markerMenu.marker.job?.orderId ? `#${markerMenu.marker.job?.orderId}` : EMPTY_VALUE}
                      </Typography>

                      <Typography className={cx('titleSub')}>
                        {getFullName({
                          firstName: markerMenu.marker.job?.client?.firstName,
                          lastName: markerMenu.marker.job?.client?.lastName,
                        }) || EMPTY_VALUE}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Menu>
            )}
          </GoogleMap>
        )}

        <Grid2 container spacing={10}>
          <BaseTable
            dataSource={listJob || []}
            currentPage={pagination?.page}
            totalPage={pagination?.totalPages}
            columns={columnsJobs(t, handleDetailOrder)}
            onPageChange={handlePageChange}
            onRowClick={handleView}
          />

          <Grid2 size={12}>
            <Stack direction={'row'} justifyContent={'space-between'} spacing={5} className={cx('boxActive')}>
              <Typography className={cx('title')}>{t('statistic_total_truck_total_title')}</Typography>
              <Stack direction={'row'} spacing={5}>
                <img
                  src={icons.menuIconVehicleInactive}
                  alt={t('common_img_text_alt', { ns: 'common/base' })}
                  className={cx('icon')}
                />
                <Typography className={cx('title')}>
                  {t('statistic_total_truck_available', {
                    total: positionTruck.length,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Grid2>
        </Grid2>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default DeliveryMap;
